import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import About from "./Components/About";
import Experience from "./Components/experience/experience";
import Skills from "./Components/Skills";
import ReferIcon from "../../assets/svg/ReferIcon";
import TrackingIcon from "../../assets/svg/TrackingIcon";
import ShareIconOutline from "../../assets/svg/ShareIconOutline";
import LikeIconLeft from "../../assets/svg/LikeIconLeft";
import ContactDetails from "./Components/ContactDetails";
import { useSelector } from "react-redux";
import {
  getCandidateDetails,
  getSkills,
  updateCandidate,
  getCandidateProfilePdfData,
} from "../../endpoints/candidate.service";
import { useDispatch } from "react-redux";
import { setUserData } from "../../store/actions/auth.action";
import { candidateStatus, userTypes } from "../../utils/constants";
import { useSnackbar } from "notistack";
import Education from "./Components/Education";
import { getExperienceList } from "../../endpoints/experience.service";
import LoadingAnimation from "../../assets/svg/LoadingAnimation";
import { colors } from "../../utils/theme";
import { Controller, FormProvider, useForm } from "react-hook-form";
import CustomInput from "../../components/CustomInput/CustomInput";
import { Cropper } from "react-cropper";
import "cropperjs/dist/cropper.css";
import { profileImage, uploadUrl } from "../../endpoints/images.service";

import ContactIcon from "../../assets/svg/ContactIcon";
import EducationIcon from "../../assets/svg/EducationIcon";
import ExperienceIcon from "../../assets/svg/ExperienceIcon";
import AboutIcon from "../../assets/svg/AboutIcon";
import SkillsIconn from "../../assets/svg/SkillsIconn";
import { isNullOrEmpty } from "../../utils/helperFunction";
import { pdf } from "@react-pdf/renderer";
import ResumePdfDocument from "./ResumePdfDocument";
import {
  BriefcaseBusiness,
  ChevronDown,
  MapPin,
  PencilLine,
} from "lucide-react";
import dayjs from "dayjs";
import Experience2 from "./Components/experience/experience2";

const availabilityStatus = Object.entries(candidateStatus).map(
  ([_, value]) => ({
    title: value,
    id: value,
  }),
);

const tabs = [
  {
    label: "About",
    icon: <AboutIcon color="white" />,
  },
  {
    label: "Work Experience",
    icon: <ExperienceIcon color="white" />,
  },
  {
    label: "Skills",
    icon: <SkillsIconn color="white" />,
  },
  {
    label: "Education & Certifications",
    icon: <EducationIcon color="white" />,
  },
  {
    label: "Contact Details",
    icon: <ContactIcon color="white" />,
  },
];

const RenderBody = forwardRef(
  (
    {
      activeTab,
      isEditing,
      setEditingTab,
      setIsEditing,
      handleSave,
      handleEditClose,
      candidateId,
    },
    ref,
  ) => {
    switch (activeTab) {
      case 0:
        return (
          <About
            ref={ref}
            isEditing={isEditing}
            setEditingTab={setEditingTab}
            setIsEditing={setIsEditing}
            handleSave={handleSave}
          />
        );
      case 1:
        return (
          <Experience2
            ref={ref}
            isEditing={isEditing}
            setEditingTab={setEditingTab}
            handleEditClose={handleEditClose}
            candidateId={candidateId}
          />
        );
      case 2:
        return (
          <Skills
            isEditing={isEditing}
            setEditingTab={setEditingTab}
            candidateId={candidateId}
          />
        );
      case 3:
        return (
          <Education
            ref={ref}
            isEditing={isEditing}
            setEditingTab={setEditingTab}
          />
        );
      case 4:
        return (
          <ContactDetails
            ref={ref}
            isEditing={isEditing}
            setEditingTab={setEditingTab}
            handleSave={handleSave}
          />
        );
      default:
        return <div />;
    }
  },
);
RenderBody.displayName = "RenderBody";

const ProfilePage2 = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef();
  const cropperRef = useRef(null);
  const filePickerRef = useRef();
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTab, setEditingTab] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingWorkStatus, setIsUpdatingWorkStatus] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [tabsStuck, setTabsStuck] = useState(false);
  const scrollContainerRef = useRef(null);
  const tabsSentinelRef = useRef(null);
  const formMethods = useForm({
    defaultValues: {
      jobTitle: user?.jobTitle,
      status: availabilityStatus?.find((x) => x.id === user?.status)?.id ?? "",
      location: user?.location,
      image: undefined,
      openProfileModel: false,
    },
  });

  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = formMethods;

  const fields = watch();
  const activeAvailabilityStatus = useMemo(
    () => availabilityStatus?.find((x) => x.id === user?.status)?.title,
    [user],
  );

  const handleTabClick = (index) => {
    setIsEditing(false);
    setEditingTab(undefined);
    setActiveTab(index);
  };

  const handleNext = () => {
    if (isEditing && (activeTab === 0 || activeTab === 4)) {
      formRef?.current();
    }
  };

  const handleSave = async (formData, enable) => {
    console.log("this is formData", formData);
    if (activeTab === 0 || activeTab === 4 || enable) {
      try {
        setIsLoading(true);
        const { success, data } = await updateCandidate(formData);
        if (success) {
          dispatch(setUserData(data));
          setIsEditing(false);
          setEditingTab(activeTab);
        }
      } catch (error) {
        enqueueSnackbar(error, {
          variant: "error",
        });
      } finally {
        setIsEditing(false);
        setIsLoading(false);
      }
    }
  };

  const handleUpdateWorkStatus = async (status) => {
    try {
      setIsUpdatingWorkStatus(true);
      const { success, data } = await updateCandidate({ status });
      if (success) {
        dispatch(setUserData(data));
      }
    } catch (error) {
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      setIsUpdatingWorkStatus(false);
    }
  };

  useEffect(() => {
    if (user) {
      setValue("jobTitle", user?.jobTitle);
      setValue("location", user?.location);
      setValue(
        "status",
        availabilityStatus?.find((x) => x.id === user?.status)?.id ?? "",
      );
    }
  }, [user]);

  const fetchCandidateDetails = useCallback(async () => {
    try {
      const { success, data } = await getCandidateDetails();
      if (success) {
        const { _id, ...rest } = data;
        dispatch(setUserData({ ...rest, candidateId: _id }));
      }
    } catch (error) {
      throw error?.message;
    }
  }, []);

  const fetchExperienceList = useCallback(async () => {
    try {
      const { success, data } = await getExperienceList();
      if (success) {
        dispatch(setUserData({ experiences: data }));
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchUserSkills = useCallback(async (candidateId = user._id) => {
    try {
      const { success, data } = await getSkills(candidateId);

      if (success) {
        dispatch(setUserData({ skills: data }));
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchCandidateDetails();
    fetchExperienceList();
    fetchUserSkills();
  }, []);

  useEffect(() => {
    const sentinel = tabsSentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setTabsStuck(!entry.isIntersecting),
      { root, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const closeModal = () => {
    setValue("openProfileModel", false);
    setValue("image", undefined);
    setValue("file", undefined);
    setValue("profileImage", undefined);
  };

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      setValue("file", file, { shouldDirty: true });
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setValue("image", reader.result, { shouldDirty: true });
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error("Error during file upload or API calls:", error);
    }
  };

  const getCroppedImage = (e) => {
    e.preventDefault();

    if (cropperRef.current) {
      const cropperInstance = cropperRef.current.cropper;
      if (cropperInstance) {
        const croppedDataUrl = cropperInstance.getCroppedCanvas().toDataURL();
        setValue("profileImage", croppedDataUrl);
        setValue("image", undefined);
      } else {
        console.error("Cropper instance is not found");
      }
    } else {
      console.error("cropperRef.current is not initialized");
    }
  };

  const updateProfile = async (payload) => {
    try {
      setIsLoading(true);
      if (payload?.profileImage) {
        const { data } = await profileImage();
        if (!data?.data || !data?.data.uploadURL) {
          throw new Error("Failed to get upload URL");
        }
        const formData = new FormData();
        const base64Data = payload?.profileImage?.split(";base64,").pop();
        const byteArray = Uint8Array.from(atob(base64Data), (c) =>
          c.charCodeAt(0),
        );
        const blob = new Blob([byteArray], { type: payload?.file?.type });
        formData.append("file", blob, payload?.file?.name);
        const fileResponse = await uploadUrl(data?.data.uploadURL, formData);
        delete payload["file"];
        delete payload["profileImage"];
        delete payload["image"];
        const picURl = fileResponse?.variants?.find(
          (x) => x.split("/").slice(-1)[0] === "public",
        );
        payload["profilePic"] = picURl;
      }
      const { success, data: userApiData } = await updateCandidate(payload);
      if (success) {
        dispatch(setUserData(userApiData));
        setValue("profileImage", undefined);
        setValue("image", undefined);
        setValue("openProfileModel", false);
      }
    } catch (error) {
      enqueueSnackbar(error, {
        variant: "error",
      });
      console.log("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      if (!user?._id) return;
      setIsDownloading(true);
      const { success, data } = await getCandidateProfilePdfData(user._id);
      if (!success) {
        throw new Error(data?.message || "Unable to generate resume PDF.");
      }
      const hasAnyContent =
        (data?.about && data.about.trim()) ||
        (Array.isArray(data?.skills) && data.skills.length > 0) ||
        (Array.isArray(data?.experience) && data.experience.length > 0) ||
        (Array.isArray(data?.education) && data.education.length > 0) ||
        (Array.isArray(data?.certifications) && data.certifications.length > 0);
      if (!hasAnyContent) {
        enqueueSnackbar(
          "Please add your profile details (About, Skills, Experience, Education, or Certifications) before generating the resume PDF.",
          { variant: "warning" },
        );
        return;
      }
      const blob = await pdf(<ResumePdfDocument data={data} />).toBlob();
      const url = window.URL.createObjectURL(blob);
      const previewWindow = window.open(url, "_blank", "noopener,noreferrer");
      if (!previewWindow) {
        const link = document.createElement("a");
        const safeFirstName = user?.firstName
          ? user.firstName.trim()
          : "candidate";
        const safeLastName = user?.lastName ? user.lastName.trim() : "profile";
        link.href = url;
        link.setAttribute(
          "download",
          `${safeFirstName}-${safeLastName}-profile.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 30000);
    } catch (error) {
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const baseUrl =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://comtracked";
  const profileLink = user?.userName
    ? `${baseUrl}/profile/${user.userName}`
    : "";

  const handleCopyProfileLink = async () => {
    if (!profileLink) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(profileLink);
      } else {
        const tempInput = document.createElement("input");
        tempInput.value = profileLink;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        tempInput.remove();
      }
      enqueueSnackbar("Profile link copied.", { variant: "success" });
    } catch (error) {
      console.error("Error copying profile link:", error);
      enqueueSnackbar("Unable to copy profile link.", { variant: "error" });
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full">
      {/* Tabs container */}
      <div
        role="tablist"
        className="flex items-center justify-evenly h-[51px] border-b-2 gap-5 bg-white rounded-t-md overflow-hidden"
      >
        {tabs?.map((item, index) => {
          const isActive = activeTab === index;
          return (
            <button
              key={index}
              role="tab"
              aria-selected={isActive}
              aria-label={item?.label}
              onClick={() => handleTabClick(index)}
              className={`flex flex-col flex-1 h-full items-center justify-center overflow-hidden transition-all duration-300 ease-out ${isActive ? "text-slate-700" : "text-[#525252] hover:bg-[#f5f5f5]"}`}
            >
              <div className="lg:hidden">{item?.icon}</div>
              <div className="font-montserrat font-medium text-xs hidden lg:flex items-center flex-1 justify-center">
                {item?.label}
              </div>
              <div
                className={`h-1 w-full rounded-full ${isActive ? "bg-slate-700" : "bg-transparent"}`}
              ></div>
            </button>
          );
        })}
      </div>

      {/* Main Body */}
      <div className="flex flex-col flex-1 rounded-b-md bg-white min-h-0 pb-6">
        <div className="flex flex-col flex-1 overflow-y-scroll no-scrollbar min-h-0 px-6">
          {/* Profile info section */}
          <div className="flex relative pl-5 my-6">
            <div className="flex gap-7 flex-1">
              {/* Profile Image */}
              <div className="flex items-center justify-center relative h-[124px] w-[124px] rounded-full bg-gray-100 border">
                {/* Edit Icon */}
                <button
                  onClick={() => setValue("openProfileModel", true)}
                  className="flex items-center justify-center bg-gray-200 h-6 w-6 rounded-full absolute right-1.5 top-1.5 hover:bg-gray-300 hover:scale-105 z-50"
                >
                  <PencilLine size={16} />
                </button>
                {user?.profilePic ? (
                  <img
                    src={user?.profilePic}
                    className="rounded-full h-full w-full object-fill z-10 max-md:h-36 max-md:w-36"
                  />
                ) : (
                  <span className="font-montserrat text-light-black font-semibold text-5xl uppercase">
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1 flex-1 mt-1.5">
                <span className="font-inter font-bold uppercase text-4xl text-slate-700">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="font-medium text-[#525252] text-xl">
                  {user?.jobTitle || "N/A"}
                </span>
                <div className="flex items-center gap-10 mt-2.5">
                  <div className="flex items-center gap-1">
                    <BriefcaseBusiness size={15} />
                    <span className="text-[#525252] font-medium text-xs">
                      Technology
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin size={15} />
                    <span className="text-[#525252] font-medium text-xs">
                      {user?.location || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Status container */}
            <div className="flex flex-col justify-between">
              <div className="flex items-center text-xs font-medium text-[#737373] gap-2">
                Status:
                <DropdownMenu>
                  <DropdownMenuTrigger className="group flex gap-1 items-center px-2 py-1.5 rounded-md text-[#007A55] bg-[#D4FFEB] transition-colors duration-200 hover:bg-[#BFF5DD] data-[state=open]:bg-[#BFF5DD]">
                    {activeAvailabilityStatus}
                    {isUpdatingWorkStatus ? (
                      <LoadingAnimation color={"black"} className="w-4 h-4" />
                    ) : (
                      <ChevronDown
                        size={16}
                        className="transition-transform duration-300 group-data-[state=open]:rotate-180"
                      />
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-white" align="start">
                    {availabilityStatus?.map((item) => {
                      return (
                        <DropdownMenuItem
                          className="text-black outline-none font-medium text-xs cursor-pointer hover:bg-gray-100 hover:text-[#007A55]"
                          key={item?.id}
                          value={item?.id}
                          onSelect={() => handleUpdateWorkStatus(item?.id)}
                        >
                          {item?.title}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex flex-row-reverse items-center mr-5">
                <div
                  onClick={() => {
                    if (user?.userType === userTypes.CANDIDATE) {
                      handleCopyProfileLink();
                    }
                  }}
                  className="flex border border-[#DDDDDD] h-[40px] w-[56px] rounded-r-md justify-center items-center cursor-pointer transition-all duration-200 ease-out hover:scale-105 hover:bg-gray-50 hover:shadow-md active:scale-95"
                >
                  <ShareIconOutline className={"w-10 h-10"} />
                </div>

                <div className="flex border-y border-l border-[#DDDDDD] h-[40px] w-[56px] justify-center items-center cursor-pointer transition-all duration-200 ease-out hover:scale-105 hover:bg-gray-50 hover:shadow-md active:scale-95">
                  <TrackingIcon className={"w-10 h-10"} fill={"#2E2E2E"} />
                </div>

                <div className="flex border-y border-[#DDDDDD] h-[40px] w-[56px] justify-center items-center cursor-pointer transition-all duration-200 ease-out hover:scale-105 hover:bg-gray-50 hover:shadow-md active:scale-95">
                  <ReferIcon className="w-7 h-7" />
                </div>

                <div className="flex border border-[#DDDDDD] h-[40px] w-[56px] rounded-l-md justify-center items-center cursor-pointer transition-all duration-200 ease-out hover:scale-105 hover:bg-gray-50 hover:shadow-md active:scale-95">
                  <LikeIconLeft className={"w-7 h-7 mt-0.5"} fill={"#2E2E2E"} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col w-full min-h-0 border-t">
            <span className="italic text-xs text-[#BABABA] ml-auto mr-1 my-3">
              Last updated at: {dayjs(user?.updatedA).format("MMMM YYYY")}
            </span>
            <FormProvider {...formMethods}>
              <RenderBody
                ref={formRef}
                activeTab={activeTab}
                isEditing={isEditing}
                editingTab={editingTab}
                handleSave={handleSave}
                setIsEditing={setIsEditing}
                handleEditClose={() => setIsEditing(false)}
                candidateId={user?._id}
              />
            </FormProvider>
          </div>
        </div>

        {fields?.openProfileModel && (
          <div
            className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-50"
            onClick={closeModal} // Close modal when clicking outside the image
          >
            <div
              className="flex overflow-hidden flex-col bg-white py-6 px-6 rounded-lg relative w-11/12 max-w-[50%]"
              onClick={(e) => e.stopPropagation()} // Prevent modal from closing when clicking inside
            >
              <div className="flex flex-1 items-center justify-between pb-6">
                <span className="font-montserrat text-charcoal font-bold text-xl">
                  Edit
                </span>
                <button
                  onClick={closeModal}
                  className="self-end text-black text-2xl font-semibold"
                >
                  X
                </button>
              </div>
              {!fields?.image && (
                <div className="flex flex-1 px-8 gap-14">
                  <div className="flex flex-col flex-1 max-w-[40%] gap-6">
                    <div className="flex self-center rounded-full h-40 w-40 bg-[#E1E0E0] items-center justify-center">
                      {fields?.profileImage || user?.profilePic ? (
                        <img
                          src={fields?.profileImage || user?.profilePic}
                          className="rounded-full h-40 w-40 object-fill"
                        />
                      ) : (
                        <span className="font-montserrat text-white font-semibold text-6xl">
                          {user?.firstName?.charAt(0)}
                          {user?.lastName?.charAt(0)}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => filePickerRef?.current?.click()}
                      className="flex  items-center border rounded-lg overflow-hidden pr-2"
                    >
                      <span className="text-sm text-[#6f6f6f] font-medium py-2.5 px-3 bg-[#E9EAF0]">
                        PNG/JPG
                      </span>
                      <span className="text-xs flex font-medium px-3 text-ngray flex-1 text-left whitespace-nowrap">
                        {fields?.file
                          ? fields?.file?.name
                          : user?.profilePic
                            ? user?.profilePic
                            : "Upload your profile picture"}
                      </span>
                    </button>
                    <input
                      ref={filePickerRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-5">
                    <CustomInput
                      label={"Job Title"}
                      name="jobTitle"
                      placeholder={"Ex: Project Manager"}
                      control={control}
                      errors={errors}
                    />
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => {
                        return (
                          <div className={`flex flex-col w-full`}>
                            <span className="font-montserrat text-charcoal font-medium text-xs mb-2">
                              Work Status
                            </span>
                            <div
                              className={`rounded-lg py-2.5 px-3 font-montserrat font-medium text-xs flex items-center border`}
                            >
                              <select
                                {...field}
                                className="w-full outline-none bg-transparent focus:outline-none focus:border-blue-500 text-charcoal"
                                name={"status"}
                                id={"status"}
                              >
                                <option value="" disabled hidden className={""}>
                                  Are you available for job?
                                </option>
                                {availabilityStatus?.map((item) => {
                                  return (
                                    <option
                                      className="text-black"
                                      key={item?.id}
                                      value={item?.id}
                                    >
                                      {item?.title}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            {errors["status"]?.message && (
                              <span className="whitespace-pre text-xs mt-2 text-orange-700">
                                {errors["status"]?.message}
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                    <CustomInput
                      label={"Location"}
                      name="location"
                      placeholder={"Ex: United Kingdom"}
                      control={control}
                      errors={errors}
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col mt-6">
                {fields?.image && (
                  <Cropper
                    src={fields?.image}
                    ref={cropperRef}
                    style={{ height: 400, width: "100%" }}
                    aspectRatio={1}
                    viewMode={2}
                    guides={false}
                    minCropBoxWidth={50} // Minimum width for the crop area
                    minCropBoxHeight={50} // Minimum height for the crop area
                  />
                )}
                <style>
                  {`
                    /* Ensure the cropper's crop box is circular */
                    .cropper-face,
                    .cropper-view-box,
                    .cropper-crop-box {
                      border-radius: 50% !important; /* Make crop area circular */
                      border: 2px solid white !important; /* Optional: Set the color of the circular crop lines */
                    }

                    .cropper-face {
                      background-color: rgba(0, 0, 0, 0.3) !important; /* Optional: Set the background color of the circular crop area */
                    }

                    .cropper-view-box {
                      border-radius: 50% !important; /* Make sure the selection area is circular */
                    }

                    .cropper-crop-box {
                      border-radius: 50% !important; /* Ensure the cropping boundary is circular */
                    }
                  `}
                </style>
                <div className="mt-10 flex justify-end">
                  <button
                    disabled={isLoading}
                    onClick={(e) => {
                      if (fields?.image) {
                        getCroppedImage(e);
                      } else {
                        updateProfile(fields);
                      }
                    }}
                    className="bg-primary items-center flex text-white px-7 py-2.5 text-xl rounded-md"
                  >
                    {isLoading && <LoadingAnimation color={colors.White} />}
                    {fields?.image ? "Crop Image" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex bg-white flex-1 h-full overflow-hidden w-full shadow-[2px_2px_12px_0px_rgba(180,180,180,0.25)] rounded-lg p-6">
      <div className="flex flex-col py-6 pr-6 w-[15%] items-center gap-5">
        <span className="text-xs font-semibold text-charcoal text-center">
          CA-FQ1478
        </span>
        {/* Open to offers */}
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center justify-center outline-none rounded-md gap-2 py-1.5 px-3 text-xs font-semibold text-white bg-green-900 w-full">
            {availabilityStatus?.find((x) => x.id === user?.status)?.title}
            {isUpdatingWorkStatus ? (
              <LoadingAnimation color={"white"} className="w-4 h-4" />
            ) : null}
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white" align="start">
            {availabilityStatus?.map((item) => {
              return (
                <DropdownMenuItem
                  className="text-black hover:cursor-pointer outline-none hover:bg-gray-100 font-medium text-xs"
                  key={item?.id}
                  value={item?.id}
                  onSelect={() => handleUpdateWorkStatus(item?.id)}
                >
                  {item?.title}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        {user?.userType === userTypes.CANDIDATE && (
          <button
            disabled={isDownloading}
            onClick={handleDownloadPdf}
            className="
        w-full
        max-w-[200px]
        flex items-center justify-center gap-2 sm:gap-3
        px-4 py-3
        rounded-lg
        text-xs font-semibold tracking-wider
        text-white
        bg-[#2f2f2f]
        shadow-lg
        transition-all duration-200
        hover:bg-[#1f1f1f] hover:-translate-y-0.5 hover:shadow-xl
        disabled:opacity-60 disabled:cursor-not-allowed
      "
          >
            <span className="text-[11px] sm:text-xs whitespace-nowrap">
              {isDownloading ? "Preparing PDF..." : "Resume PDF"}
            </span>
          </button>
        )}

        {/* Action button section */}
        <div className="flex flex-col gap-6 mt-24">
          <div
            className="group flex flex-col justify-center w-14 h-14 border rounded-md items-center cursor-pointer gap-1 bg-gray-50"
            onClick={() => {
              if (user?.userType === userTypes.CANDIDATE) {
                handleCopyProfileLink();
              }
            }}
          >
            <div className="flex border border-primary h-[22px] w-[22px] rounded-full justify-center items-center">
              <ShareIconOutline className={"w-6 h-6"} />
            </div>
            <span className="font-medium uppercase text-[8px] text-light-black">
              Share
            </span>
          </div>

          <div className="group flex flex-col justify-center w-14 h-14 border rounded-md items-center cursor-pointer gap-1 bg-gray-50">
            <div className="flex border border-primary h-[22px] w-[22px] rounded-full justify-center items-center">
              <TrackingIcon className={"w-6 h-6"} fill={"#2E2E2E"} />
            </div>
            <span className="font-medium uppercase text-[8px] text-light-black">
              Track
            </span>
          </div>

          <div className="group flex flex-col justify-center w-14 h-14 border rounded-md items-center cursor-pointer gap-1 bg-gray-50">
            <div className="flex border border-primary h-[22px] w-[22px] rounded-full justify-center items-center">
              <ReferIcon className="w-6 h-6" />
            </div>
            <span className="font-medium uppercase text-[8px] text-light-black">
              Refer
            </span>
          </div>

          <div className="group flex flex-col justify-center w-14 h-14 border rounded-md items-center cursor-pointer gap-1 bg-gray-50">
            <div className="flex border border-primary h-[22px] w-[22px] rounded-full justify-center items-center">
              <LikeIconLeft className={"w-5 h-5 mt-0.5"} fill={"#2E2E2E"} />
            </div>
            <span className="font-medium uppercase text-[8px] text-light-black">
              Likes
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col border-x flex-1 border-b">
        {/* Top container */}
        <div className="flex items-center bg-[#FAFAFA] py-2.5 px-2.5 border border-[#F7F7F7] mb-1 relative">
          {/* Image container */}
          <div className="absolute top-0 flex rounded-full h-40 w-40 bg-[#E1E0E0] items-center justify-center max-md:h-36 max-md:w-36">
            {user?.profilePic ? (
              <img
                src={user?.profilePic}
                className="rounded-full h-40 w-40 object-fill z-10 max-md:h-36 max-md:w-36"
              />
            ) : (
              <span className="font-montserrat text-white font-semibold text-6xl uppercase">
                {user?.firstName?.charAt(0)}
                {user?.lastName?.charAt(0)}
              </span>
            )}
            {/* <div
              className="flex items-center absolute top-3 right-2 bg-white justify-center rounded-full border self-start p-1"
              onClick={() => {
                setValue("openProfileModel", true);
              }}
            >
              <EditIcon className={"w-5 h-5"} fill="#4d4d4d" />
            </div> */}
          </div>

          <div className="flex justify-between flex-1 gap-1 pl-20 items-center">
            <div />
            <div className="flex flex-col gap-2.5 my-2 items-center">
              <span className="font-montserrat text-charcoal text-2xl border-b border-primary w-max pb-1 px-4 max-md:text-base">
                {user?.firstName} {user?.lastName}
              </span>
              <span className="font-montserrat text-light-black font-medium text-xs max-[900px]:w-min text-center">
                {user?.jobTitle || "N/A"}
              </span>
            </div>
            <button
              onClick={() => setValue("openProfileModel", true)}
              className="border rounded-md text-xs px-2 py-1.5 bg-white font-medium self-start hover:bg-gray-50"
            >
              Edit
            </button>
          </div>
        </div>

        {/* Main Body Wrapper */}
        <div className="flex flex-col flex-1 overflow-hidden relative">
          <div className="flex items-center ml-28 pl-20 border-b gap-4 xl:pr-10 mt-[0.8rem] justify-between pr-4">
            {tabs?.map((item, index) => {
              return (
                <button
                  key={index}
                  onClick={() => handleTabClick(index)}
                  style={{
                    borderBottom:
                      activeTab === index
                        ? "2px solid #EFBF04"
                        : "2px solid transparent",
                  }}
                  className="flex pb-1 h-full w-9 items-center justify-center lg:w-auto"
                >
                  <div className="lg:hidden">{item?.icon}</div>
                  <span className="font-montserrat text-charcoal font-medium text-xs hidden lg:block">
                    {item?.label}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto no-scrollbar pl-24">
            {activeTab !== 2 &&
              !(activeTab === 1 && isNullOrEmpty(user.experience)) && (
                <div
                  className="flex self-end h-9 items-center justify-center rounded border gap-[10px] px-2 py-1.5 mt-5 mr-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    handleNext();
                    setIsEditing(!isEditing);
                    setEditingTab(activeTab);
                  }}
                  disabled={isLoading}
                >
                  {isLoading && isEditing && (
                    <LoadingAnimation color={colors.PrimaryBlack} />
                  )}
                  <span className="font-montserrat text-charcoal font-medium text-xs">
                    {isEditing ? "Save" : "Edit"}
                  </span>
                </div>
              )}
            <RenderBody
              ref={formRef}
              activeTab={activeTab}
              isEditing={isEditing}
              editingTab={editingTab}
              handleSave={handleSave}
              handleEditClose={() => setIsEditing(false)}
              candidateId={user._id}
            />
          </div>
        </div>

        {fields?.openProfileModel && (
          <div
            className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-50"
            onClick={closeModal} // Close modal when clicking outside the image
          >
            <div
              className="flex overflow-hidden flex-col bg-white py-6 px-6 rounded-lg relative w-11/12 max-w-[50%]"
              onClick={(e) => e.stopPropagation()} // Prevent modal from closing when clicking inside
            >
              <div className="flex flex-1 items-center justify-between pb-6">
                <span className="font-montserrat text-charcoal font-bold text-xl">
                  Edit
                </span>
                <button
                  onClick={closeModal}
                  className="self-end text-black text-2xl font-semibold"
                >
                  X
                </button>
              </div>
              {!fields?.image && (
                <div className="flex flex-1 px-8 gap-14">
                  <div className="flex flex-col flex-1 max-w-[40%] gap-6">
                    <div className="flex self-center rounded-full h-40 w-40 bg-[#E1E0E0] items-center justify-center">
                      {fields?.profileImage || user?.profilePic ? (
                        <img
                          src={fields?.profileImage || user?.profilePic}
                          className="rounded-full h-40 w-40 object-fill"
                        />
                      ) : (
                        <span className="font-montserrat text-white font-semibold text-6xl">
                          {user?.firstName?.charAt(0)}
                          {user?.lastName?.charAt(0)}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => filePickerRef?.current?.click()}
                      className="flex  items-center border rounded-lg overflow-hidden pr-2"
                    >
                      <span className="text-sm text-[#6f6f6f] font-medium py-2.5 px-3 bg-[#E9EAF0]">
                        PNG/JPG
                      </span>
                      <span className="text-xs flex font-medium px-3 text-ngray flex-1 text-left whitespace-nowrap">
                        {fields?.file
                          ? fields?.file?.name
                          : user?.profilePic
                            ? user?.profilePic
                            : "Upload your profile picture"}
                      </span>
                    </button>
                    <input
                      ref={filePickerRef}
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-5">
                    <CustomInput
                      label={"Job Title"}
                      name="jobTitle"
                      placeholder={"Ex: Project Manager"}
                      control={control}
                      errors={errors}
                    />
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => {
                        return (
                          <div className={`flex flex-col w-full`}>
                            <span className="font-montserrat text-charcoal font-medium text-xs mb-2">
                              Work Status
                            </span>
                            <div
                              className={`rounded-lg py-2.5 px-3 font-montserrat font-medium text-xs flex items-center border`}
                            >
                              <select
                                {...field}
                                className="w-full outline-none bg-transparent focus:outline-none focus:border-blue-500 text-charcoal"
                                name={"status"}
                                id={"status"}
                              >
                                <option value="" disabled hidden className={""}>
                                  Are you available for job?
                                </option>
                                {availabilityStatus?.map((item) => {
                                  return (
                                    <option
                                      className="text-black"
                                      key={item?.id}
                                      value={item?.id}
                                    >
                                      {item?.title}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            {errors["status"]?.message && (
                              <span className="whitespace-pre text-xs mt-2 text-orange-700">
                                {errors["status"]?.message}
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                    <CustomInput
                      label={"Location"}
                      name="location"
                      placeholder={"Ex: United Kingdom"}
                      control={control}
                      errors={errors}
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col mt-6">
                {fields?.image && (
                  <Cropper
                    src={fields?.image}
                    ref={cropperRef}
                    style={{ height: 400, width: "100%" }}
                    aspectRatio={1}
                    viewMode={2}
                    guides={false}
                    minCropBoxWidth={50} // Minimum width for the crop area
                    minCropBoxHeight={50} // Minimum height for the crop area
                  />
                )}
                <style>
                  {`
                    /* Ensure the cropper's crop box is circular */
                    .cropper-face,
                    .cropper-view-box,
                    .cropper-crop-box {
                      border-radius: 50% !important; /* Make crop area circular */
                      border: 2px solid white !important; /* Optional: Set the color of the circular crop lines */
                    }

                    .cropper-face {
                      background-color: rgba(0, 0, 0, 0.3) !important; /* Optional: Set the background color of the circular crop area */
                    }

                    .cropper-view-box {
                      border-radius: 50% !important; /* Make sure the selection area is circular */
                    }

                    .cropper-crop-box {
                      border-radius: 50% !important; /* Ensure the cropping boundary is circular */
                    }
                  `}
                </style>
                <div className="mt-10 flex justify-end">
                  <button
                    disabled={isLoading}
                    onClick={(e) => {
                      if (fields?.image) {
                        getCroppedImage(e);
                      } else {
                        updateProfile(fields);
                      }
                    }}
                    className="bg-primary items-center flex text-white px-7 py-2.5 text-xl rounded-md"
                  >
                    {isLoading && <LoadingAnimation color={colors.White} />}
                    {fields?.image ? "Crop Image" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage2;
